import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    toast.success('Message sent', {
      description: 'We will get back to you within 24 hours.'
    });
    (e.target as HTMLFormElement).reset();
    setTimeout(() => setSubmitted(false), 3000);
  };
  const contactDetails = [{
    icon: Phone,
    label: 'Phone',
    value: '+960 330 1234',
    href: 'tel:+9603301234'
  }, {
    icon: MessageCircle,
    label: 'WhatsApp',
    value: '+960 777 1234',
    href: 'https://wa.me/9607771234'
  }, {
    icon: Mail,
    label: 'Email',
    value: 'hello@beachshop.mv',
    href: 'mailto:hello@beachshop.mv'
  }, {
    icon: MapPin,
    label: 'Address',
    value: 'Boduthakurufaanu Magu, Malé 20026, Maldives',
    href: 'https://maps.google.com/?q=Male+Maldives'
  }, {
    icon: Clock,
    label: 'Opening Hours',
    value: 'Sat – Thu: 9:00 – 22:00  •  Fri: 14:00 – 22:00'
  }];
  return <div className="bg-gray-50 min-h-screen pb-16" data-id="element-435">
      {/* Hero */}
      <section className="bg-white border-b border-gray-200" data-id="element-436">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-437">
          <span className="text-xs font-semibold uppercase tracking-widest text-red-600" data-id="element-438">
            Get in Touch
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4" data-id="element-439">
            We'd love to hear from you
          </h1>
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed" data-id="element-440">
            Questions about a product, an order, or a delivery to your island?
            Our team is happy to help — most messages get a reply within a few
            hours during business days.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-441">
        {/* Contact Details */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-442">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-443">
            Contact Details
          </h2>
          <ul className="space-y-5" data-id="element-444">
            {contactDetails.map(item => <li key={item.label} className="flex items-start gap-4" data-id="element-445">
                <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0" data-id="element-446">
                  <item.icon className="w-5 h-5" data-id="element-447" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-448">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1" data-id="element-449">
                    {item.label}
                  </div>
                  {item.href ? <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="text-sm text-gray-900 hover:text-red-600 break-words" data-id="element-450">
                      {item.value}
                    </a> : <div className="text-sm text-gray-900" data-id="element-451">{item.value}</div>}
                </div>
              </li>)}
          </ul>

          <div className="mt-8 pt-6 border-t border-gray-100 text-sm text-gray-500 leading-relaxed" data-id="element-452">
            For wholesale, partnership, or press inquiries, please contact us at{' '}
            <a href="mailto:partners@beachshop.mv" className="text-red-600 hover:underline" data-id="element-453">
              partners@beachshop.mv
            </a>
            .
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-454">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-455">
            Send us a Message
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-456">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-457">
              <div data-id="element-458">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-459">
                  Name
                </label>
                <input type="text" id="name" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-460" />
              </div>
              <div data-id="element-461">
                <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-462">
                  Email
                </label>
                <input type="email" id="contact-email" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-463" />
              </div>
            </div>
            <div data-id="element-464">
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-465">
                Subject
              </label>
              <select id="subject" required className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-466">
                <option value="" data-id="element-467">Select a topic…</option>
                <option data-id="element-468">Order enquiry</option>
                <option data-id="element-469">Product question</option>
                <option data-id="element-470">Delivery / shipping</option>
                <option data-id="element-471">Returns & refunds</option>
                <option data-id="element-472">Wholesale</option>
                <option data-id="element-473">Something else</option>
              </select>
            </div>
            <div data-id="element-474">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-475">
                Message
              </label>
              <textarea id="message" required rows={5} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none" data-id="element-476" />
            </div>
            <button type="submit" disabled={submitted} className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:bg-gray-400" data-id="element-477">
              {submitted ? 'Sent ✓' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </div>;
}